import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  RiEditLine,
  RiDeleteBinLine,
  RiCloseLine,
  RiCheckLine,
} from "react-icons/ri";
import { FiUploadCloud } from "react-icons/fi";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const BannerCardSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg animate-pulse">
      <div className="h-6 w-24 bg-gray-200 rounded mb-3"></div>
      <div className="h-8 w-3/4 bg-gray-300 rounded mb-2"></div>
      <div className="h-4 w-full bg-gray-200 rounded"></div>
    </div>
  );
};

const BannerData = () => {
  const [banners, setBanners] = useState([]);
  const [selectedPage, setSelectedPage] = useState("rental");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    bgImage: "",
  });

  const [errors, setErrors] = useState({});

  const pageOptions = [
    { value: "rental", label: "Rentals" },
    { value: "membership", label: "Memberships" },
    { value: "sports-academy", label: "Academies" },
    { value: "leagues", label: "Leagues" },
    { value: "bar-restaurant", label: "Bar & Restaurant" },
    { value: "clubworx", label: "Book Now" },
    { value: "contact", label: "Contact" },
    { value: "faqs", label: "Faqs" },
  ];

  // Fetch all banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/page-banner/get-all-banner`
      );
      if (data?.banners) {
        setBanners(data.banners);
      }
    } catch (err) {
      console.error("Failed to fetch banners:", err);
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  // Upload image to Cloudinary
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file.");
      return;
    }

    try {
      setUploading(true);
      toast.loading("Uploading image...");

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const uploadResponse = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        formDataUpload
      );

      const imageUrl = uploadResponse.data.secure_url;
      setFormData({ ...formData, bgImage: imageUrl });
      toast.dismiss();
      toast.success("Image uploaded successfully ✅");
    } catch (error) {
      console.error(error);
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.subtitle.trim()) newErrors.subtitle = "Subtitle is required";
    if (!formData.bgImage) newErrors.bgImage = "Banner image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear form
  const clearForm = () => {
    setFormData({ title: "", subtitle: "", bgImage: "" });
    setErrors({});
  };

  // Create new banner
  const handleCreateBanner = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      pageName: selectedPage,
      title: formData.title,
      subtitle: formData.subtitle,
      bgImage: formData.bgImage,
    };

    try {
      setIsSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/page-banner/create-banner`,
        payload
      );
      toast.success("Banner created successfully");
      clearForm();
      fetchBanners();
    } catch (error) {
      console.error("Error creating banner:", error);
      toast.error(error.response?.data?.message || "Failed to create banner");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Separate state for modal form
  const [modalFormData, setModalFormData] = useState({
    title: "",
    subtitle: "",
    bgImage: "",
  });

  const [modalErrors, setModalErrors] = useState({});

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setModalFormData({ ...modalFormData, [name]: value });
    setModalErrors({ ...modalErrors, [name]: "" });
  };

  const validateModalForm = () => {
    let newErrors = {};
    if (!modalFormData.title.trim()) newErrors.title = "Title is required";
    if (!modalFormData.subtitle.trim())
      newErrors.subtitle = "Subtitle is required";
    if (!modalFormData.bgImage) newErrors.bgImage = "Banner image is required";
    setModalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleModalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file.");
      return;
    }

    try {
      setUploading(true);
      toast.loading("Uploading image...");

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const uploadResponse = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        formDataUpload
      );

      const imageUrl = uploadResponse.data.secure_url;
      setModalFormData({ ...modalFormData, bgImage: imageUrl });
      toast.dismiss();
      toast.success("Image uploaded successfully ✅");
    } catch (error) {
      console.error(error);
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Open edit modal
  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setModalFormData({
      title: banner.title,
      subtitle: banner.subtitle,
      bgImage: banner.bgImage,
    });
    setModalErrors({});
    setIsModalOpen(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
    clearForm();
  };

  // Update banner
  const handleUpdateBanner = async (e) => {
    e.preventDefault();
    if (!validateModalForm()) return;

    const payload = {
      title: modalFormData.title,
      subtitle: modalFormData.subtitle,
      bgImage: modalFormData.bgImage,
    };

    try {
      setIsSubmitting(true);
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/page-banner/update-banner/${editingBanner.pageName}`,
        payload
      );
      toast.success("Banner updated successfully");
      fetchBanners();
      closeEditModal();
    } catch (error) {
      console.error("Error updating banner:", error);
      toast.error("Failed to update banner");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete banner
  const deleteBanner = async (pageName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/page-banner/delete-page-banner/${pageName}`
        );
        fetchBanners();
        Swal.fire({
          title: "Deleted!",
          text: "Banner has been removed successfully.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (error) {
        console.error("Error deleting banner:", error);
        toast.error("Failed to delete banner");
      }
    }
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
          Page Banner Management
        </h1>
      </div>

      {/* Section 1: Create New Banner */}
      <div className="bg-white rounded-2xl mb-8">
        <form onSubmit={handleCreateBanner}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Page Selection */}
            <div>
              <label className="block text-sm font-semibold text-secondaryTextColor mb-4">
                Select Page
              </label>
              <div className="space-y-3">
                {pageOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPage === option.value
                        ? "border-primaryBtnColor bg-gradient-to-r from-primaryBtnColor/10 to-primaryBtnHoverColor/10"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="page"
                      value={option.value}
                      checked={selectedPage === option.value}
                      onChange={(e) => setSelectedPage(e.target.value)}
                      className="w-5 h-5 text-primaryBtnColor focus:ring-primaryBtnColor"
                    />
                    <span className="ml-3 text-primaryTextColor font-medium">
                      {option.label}
                    </span>
                    {selectedPage === option.value && (
                      <RiCheckLine className="ml-auto text-primaryBtnColor text-xl" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Right Side - Title, Subtitle, and Image */}
            <div>
              <div className="space-y-4">
                {/* Title Input */}
                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
                    Banner Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter banner title"
                    className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                  )}
                </div>

                {/* Subtitle Input */}
                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
                    Banner Subtitle
                  </label>
                  <textarea
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    placeholder="Enter banner subtitle"
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor resize-none"
                  />
                  {errors.subtitle && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.subtitle}
                    </p>
                  )}
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
                    Banner Image
                  </label>

                  {formData.bgImage && (
                    <div className="relative w-full mb-2 h-32 rounded-lg overflow-hidden border border-gray-300">
                      <img
                        src={formData.bgImage}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="w-full border border-gray-300 rounded-lg p-2 text-primaryTextColor cursor-pointer"
                  />
                  {errors.bgImage && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.bgImage}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || uploading}
                  className="w-full bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-3 px-6 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Creating..." : "Add Banner"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Section 2: All Banners */}
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, idx) => (
                <BannerCardSkeleton key={idx} />
              ))
            : banners.map((banner) => (
                <div
                  key={banner._id}
                  className="relative bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-shadow group"
                >
                  {/* Banner Image */}
                  {banner.bgImage && (
                    <div className="w-full h-32 overflow-hidden">
                      <img
                        src={banner.bgImage}
                        alt={banner.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-4">
                    {/* Page Name Badge */}
                    <div className="inline-block bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase">
                      {banner.pageName.replace("-", " ")}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-primaryTextColor mb-2 line-clamp-2">
                      {banner.title}
                    </h3>

                    {/* Subtitle */}
                    <p className="text-secondaryTextColor text-sm line-clamp-2">
                      {banner.subtitle}
                    </p>
                  </div>

                  {/* Edit/Delete Icons */}
                  <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(banner)}
                      className="bg-white bg-opacity-90 p-2 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors shadow-md"
                    >
                      <RiEditLine />
                    </button>
                    <button
                      onClick={() => deleteBanner(banner.pageName)}
                      className="bg-white bg-opacity-90 p-2 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-md"
                    >
                      <RiDeleteBinLine />
                    </button>
                  </div>
                </div>
              ))}
        </div>

        {!loading && banners.length === 0 && (
          <div className="text-center py-12">
            <p className="text-secondaryTextColor text-lg">
              No banners found. Create your first banner above!
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg border border-primaryTextColor shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primaryTextColor">
                Edit Banner
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-primaryBtnHoverColor"
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateBanner} className="p-6 space-y-4">
              {/* Page Name (Read-only) */}
              <div>
                <label className="block text-sm text-secondaryTextColor mb-2">
                  Page Name
                </label>
                <input
                  type="text"
                  value={editingBanner?.pageName
                    .replace("-", " ")
                    .toUpperCase()}
                  disabled
                  className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm text-secondaryTextColor mb-2">
                  Banner Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={modalFormData.title}
                  onChange={handleModalChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
                {modalErrors.title && (
                  <p className="text-red-500 text-sm mt-1">
                    {modalErrors.title}
                  </p>
                )}
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm text-secondaryTextColor mb-2">
                  Banner Subtitle
                </label>
                <textarea
                  name="subtitle"
                  value={modalFormData.subtitle}
                  onChange={handleModalChange}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg p-3 resize-none"
                />
                {modalErrors.subtitle && (
                  <p className="text-red-500 text-sm mt-1">
                    {modalErrors.subtitle}
                  </p>
                )}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm text-secondaryTextColor mb-2">
                  Banner Image
                </label>

                {modalFormData.bgImage && (
                  <div className="relative w-full mb-2 h-32 rounded-lg overflow-hidden border border-gray-300">
                    <img
                      src={modalFormData.bgImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleModalImageUpload}
                  disabled={uploading}
                  className="w-full border border-gray-300 rounded-lg p-2 text-primaryTextColor cursor-pointer"
                />
                {modalErrors.bgImage && (
                  <p className="text-red-500 text-sm mt-1">
                    {modalErrors.bgImage}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="bg-gray-200 text-secondaryTextColor py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting || uploading}
                  type="submit"
                  className="bg-primaryBtnColor text-white flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Updating..." : "Update Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerData;
const BannerSkeleton = () => {
  return (
    <div className="relative w-full h-36 sm:h-52 md:h-60 lg:h-80 px-4 md:px-8 lg:px-12 animate-pulse">
      <div className="absolute inset-0 bg-gray-300/50"></div>

      <div className="relative z-10 flex flex-col justify-center h-full">
        <div className="h-8 w-40 bg-gray-400/60 rounded-md mb-4"></div>
        <div className="h-4 w-64 bg-gray-400/50 rounded-md"></div>
      </div>
    </div>
  );
};
